import React from 'react';

import icon from '@assets/img/icons/company-info.svg';
import { Layout } from '@components/Layout';
import { MembershipContainer } from '@components/settings/MembershipContainer';
import {
  MembershipNameTextDisplay,
  MembershipTextDisplay,
} from '@components/utilities/MembershipForm/Form';
import { strings as membershipFormStrings } from '@data/membershipForm';
import { useCompany } from '@hooks/queries/useCompany';
import { useUser } from '@hooks/queries/useUser';
import { countryStrings } from '@models/Country';
import { MembershipFormFields } from '@models/MembershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';

const strings = createStrings({
  ja: {
    title: membershipFormStrings.companyInfo,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    padding: '1rem',
  },
  contactToChangeInformation: {
    ...common.middleText,
    ...common.textError,
    padding: '1rem',
  },
}));

export const CompanyInfo: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const user = useUser();
  const company = useCompany();

  return (
    <Layout
      loading={user.isLoading || company.isLoading}
      title={strings.title}
      breadcrumbs={['/utilities/Dashboard', '/settings/Membership/CompanyInfo']}
    >
      {() => {
        const values = MembershipFormFields.shared.mapCompanyUserToMembershipForm(
          company.data!,
          user.data!,
        );

        return (
          <MembershipContainer>
            <div className={classes.container}>
              <MembershipTextDisplay
                field={MembershipFormFields.shared.companyName}
                value={values.companyName}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.englishName}
                value={values.englishName}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.corporateNumber}
                value={values.corporateNumber}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.zipcode}
                value={values.zipcode}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.country}
                value={countryStrings[values.country]}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.address1}
                value={values.address1}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.address2}
                value={values.address2}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.phone}
                value={values.phone}
              />
              <MembershipTextDisplay field={MembershipFormFields.shared.fax} value={values.fax} />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.startDate}
                value={values.startDate}
              />
              <MembershipNameTextDisplay
                lastNameField={MembershipFormFields.shared.representerLastName}
                lastName={values.representerLastName}
                firstNameField={MembershipFormFields.shared.representerFirstName}
                firstName={values.representerFirstName}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.legalCapital}
                value={values.legalCapital}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.industry}
                value={values.industry}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.employeeCount}
                value={values.employeeCount}
              />
              <MembershipTextDisplay
                field={MembershipFormFields.shared.transactionCondition}
                value={values.transactionCondition}
              />

              <div className={classes.contactToChangeInformation}>
                {membershipFormStrings.contactToChangeInformation}
              </div>
            </div>
          </MembershipContainer>
        );
      }}
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  icon,
  order: 1,
};
